import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { AdminDemoData } from '@platform/client';
import { ProjectRoleType } from '@platform/client/schemas';
import { FormsInputDropdown, InlineFormInput } from '@platform/components';
import { AdminFormModal, ModalStage } from '../../AdminFormModal';

type NewDemoProjectFormData = {
  representativeId: number;
  roleType?: ProjectRoleType;
};

export const CreateDemoProjectModal = (props: { onClose: () => void }) => {
  const [modalStage, setModalStage] = useState<ModalStage>('form');
  const [successText, setSuccessText] = useState<string>();
  const { control, formState, register, handleSubmit } = useForm<NewDemoProjectFormData>({
    mode: 'onChange',
  });

  const createProjectMutator = AdminDemoData.useCreateDemoProject({
    mutation: {
      onMutate: () => setModalStage('submitting'),
      onError: () => setModalStage('error'),
      onSuccess: (data) => {
        setSuccessText(
          `Project #${data.projectId} (${data.projectName}) created for organization #${data.organizationId}.`,
        );
        setModalStage('success');
      },
    },
  });

  const onSubmit: SubmitHandler<NewDemoProjectFormData> = (data) => {
    createProjectMutator.mutate({
      data: { representativeId: data.representativeId, roleType: data.roleType || null },
    });
  };

  return (
    <AdminFormModal
      title='Create Demo Project'
      onClose={props.onClose}
      modalStage={modalStage}
      successText={successText}
      submitCTA={{
        text: 'Create',
        callback: () => {
          handleSubmit(onSubmit)();
        },
      }}
    >
      <div className='space-y-2'>
        <p className='text-sm text-gray-600'>
          Use the representative ID shown after creating a demo organization — the organization is
          derived from the representative, so there's no risk of a mismatched pair.
        </p>
        <InlineFormInput
          register={() =>
            register('representativeId', { required: 'Representative ID is required' })
          }
          label='Representative ID'
          placeholder='Enter representative id'
          errors={formState.errors.representativeId}
        />
        <FormsInputDropdown
          type='default'
          control={control}
          name='roleType'
          label='Project Type'
          placeholder='Consultant (default)'
          items={[
            { text: 'Consultant', textValue: 'consultant' },
            { text: 'Coach', textValue: 'coach' },
            { text: 'Mentor', textValue: 'mentor' },
            { text: 'Trainer', textValue: 'trainer' },
            { text: 'Call', textValue: 'call' },
          ]}
          error={undefined}
        />
      </div>
    </AdminFormModal>
  );
};
